// 「対象者(受診者)」= 代理予約・代理会計の対象(本人 + サブメンバー)を統一的に扱う。
// レコード(会計/サブスク/予約/診察券)は生成型の proxy_group_member_id で所有者を表す。

import { useGroup } from './group'
import { useMe } from './me'
import type { GroupMember } from './group'
import type { Me } from './me'

export type SubProfileTargetKind = 'main' | 'member'

export interface SubProfileTarget {
  kind: SubProfileTargetKind
  /** 対象者の proxy_group_member_id。本人(main)=メインの pgm id、member=SubMember.id。 */
  proxyGroupMemberId: number
  displayName: string
  displayNameRuby: string
  /** 生年月日('YYYY-MM-DD')。メンバーのみ供給される(本人は未設定)。 */
  birthday: string | null
}

const buildMainTarget = (me: Me): SubProfileTarget => ({
  kind: 'main',
  proxyGroupMemberId: me.proxyGroupMemberId,
  // 旧構造の me は useMe().get() が再取得で弾くため、ここでは新構造を前提に直参照する。
  displayName: me.proxyGroupMember.profile.name ?? '',
  displayNameRuby: me.proxyGroupMember.profile.nameRuby ?? '',
  birthday: null,
})

const buildMemberTarget = (member: GroupMember): SubProfileTarget => ({
  kind: 'member',
  proxyGroupMemberId: member.id,
  displayName: member.name,
  displayNameRuby: member.nameRuby,
  birthday: member.birthday,
})

/**
 * レコードの proxy_group_member_id を対象者一覧(getTargets の結果)へ突き合わせて解決する純関数。
 * - 未設定(undefined/null) → null(対象者不明。バッジ非表示で扱う)
 * - 一致する対象者が無い(脱退済みメンバー宛等) → null
 */
export const resolveTarget = (
  proxyGroupMemberId: number | null | undefined,
  targets: SubProfileTarget[]
): SubProfileTarget | null => {
  if (proxyGroupMemberId == null) return null
  return targets.find((t) => t.proxyGroupMemberId === proxyGroupMemberId) ?? null
}

/**
 * 「保有一覧(診察券など対象者でグルーピングし必ず表示する画面)」用の解決。
 * - 解決できればそれを返す
 * - 未提供(undefined/null) → 本人(main)へ寄せる(未提供で一覧から消えるのを防ぐ)
 * - id 指定ありで一致なし(脱退済み) → null(本人へは寄せない=他人の診察券を本人扱いしない)
 */
export const resolveOwnedTarget = (
  proxyGroupMemberId: number | null | undefined,
  targets: SubProfileTarget[]
): SubProfileTarget | null => {
  const resolved = resolveTarget(proxyGroupMemberId, targets)
  if (resolved) return resolved
  if (proxyGroupMemberId == null) return targets.find((t) => t.kind === 'main') ?? null
  return null
}

export const useSubProfile = () => {
  /** 対象者一覧(本人を先頭に、グループがあればサブメンバーを続ける)。 */
  const getTargets = async (): Promise<SubProfileTarget[]> => {
    // me と group は独立に取得できるため並列化(各リスト画面の表示前 await を短縮)。
    const [me, group] = await Promise.all([useMe().get(), useGroup().get()])
    const targets: SubProfileTarget[] = [buildMainTarget(me)]
    if (group) targets.push(...group.subMembers.map(buildMemberTarget))
    return targets
  }

  return { getTargets, resolveTarget, resolveOwnedTarget }
}
